"use client";

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import type { ChartConfig } from "@/components/ui/chart";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@/components/ui/chart";
import { Line, LineChart, CartesianGrid, XAxis, YAxis } from "recharts";
import { request } from "@/lib/request";
import { processTrendData } from "@/lib/trendDataProcessor";
import { usePersistentState } from "@/hooks/usePersistentState";

interface ChartDataItem {
  name: string;
  value: number;
}

interface TrendData {
  x: string[];
  [key: string]: any;
}

const chartConfig = {
  value: {
    label: "数值",
    color: "var(--chart-1)",
  },
} satisfies ChartConfig;

const CHART_TYPES = [
  { key: "maxMaxy2", label: "maxMaxy2", type: "line" },
  // 最常用的指标放前面
  { key: "top4s", label: "top4s", type: "line" },
  { key: "maxMiny2", label: "maxMiny2", type: "line" },
  { key: "gaps", label: "gaps", type: "line" },
  { key: "g1", label: "g1", type: "line" },

  // 其他指标
  { key: "g2", label: "g2", type: "line" },
  { key: "top1s", label: "top1s", type: "line" },
  { key: "top2s", label: "top2s", type: "line" },
  { key: "top3s", label: "top3s", type: "line" },
  { key: "y", label: "y", type: "line" },
  { key: "g1a", label: "g1a", type: "line" },
  { key: "g2a", label: "g2a", type: "line" },
  { key: "maxMiny", label: "maxMiny", type: "line" },
  { key: "minMiny", label: "minMiny", type: "line" },
  { key: "maxMaxy", label: "maxMaxy", type: "line" },
  { key: "minMaxy", label: "minMaxy", type: "line" },
  { key: "minMiny2", label: "minMin2", type: "line" },
  { key: "maxMiny3", label: "maxMin3", type: "line" },
  { key: "minMiny3", label: "minMin3", type: "line" },
  { key: "maxMaxy3", label: "maxMax3", type: "line" },
  { key: "minMaxy3", label: "minMax3", type: "line" },
] as const;

// 2 列网格中渲染的指标(独占一行/显式成对渲染的不在此列)
const GRID_KEYS = [
  "gaps",
  "top1s",
  "top2s",
  "top3s",
  "y",
  "g1a",
  "g2a",
  "maxMiny",
  "minMiny",
  "maxMaxy",
  "minMaxy",
  "minMiny3",
  "minMaxy3",
  "maxMaxy3",
] as const;

export default function Trend() {
  const [platform, setPlatform] = usePersistentState("admin:trend:platform", "xr");
  const [timeRange, setTimeRange] = usePersistentState("admin:trend:time-range", "3h");
  const [chartData, setChartData] = useState<Record<string, ChartDataItem[]>>(
    {},
  );
  const [loading, setLoading] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const resp = await request("/common/apis/v1/trend", {
        plat: platform,
        h: timeRange,
      });

      // Check if this is ES response format
      if (resp?.data?.hits?.hits) {
        console.log(
          "Processing ES data, hits count:",
          resp.data.hits.hits.length,
        );

        // Process ES data using the processor
        const processedData = processTrendData(resp.data, platform);

        console.log("Processed data keys:", Object.keys(processedData));
        console.log(
          "top4s length:",
          processedData.top4s?.length,
          "sample:",
          processedData.top4s?.slice(0, 3),
        );
        console.log(
          "maxMiny2 length:",
          processedData.maxMiny2?.length,
          "sample:",
          processedData.maxMiny2?.slice(0, 3),
        );
        console.log(
          "gaps length:",
          processedData.gaps?.length,
          "sample:",
          processedData.gaps?.slice(0, 3),
        );
        console.log(
          "g1 length:",
          processedData.g1?.length,
          "sample:",
          processedData.g1?.slice(0, 3),
        );

        // Format data for charts
        const formattedData: Record<string, ChartDataItem[]> = {};

        CHART_TYPES.forEach(({ key }) => {
          if (processedData[key] && Array.isArray(processedData[key])) {
            formattedData[key] = processedData.x.map(
              (time: string, index: number) => ({
                name: time,
                value: processedData[key][index] || 0,
              }),
            );
            console.log(
              `Chart ${key}: ${formattedData[key].length} points, first value:`,
              formattedData[key][0],
            );
          } else {
            console.warn(`Missing data for ${key}`);
          }
        });

        console.log("Charts created:", Object.keys(formattedData));
        setChartData(formattedData);
      } else if ((resp as any)?.x && Array.isArray((resp as any).x)) {
        // Data already processed, use directly
        const formattedData: Record<string, ChartDataItem[]> = {};
        const respData = resp as any;

        CHART_TYPES.forEach(({ key }) => {
          if (respData[key] && Array.isArray(respData[key])) {
            formattedData[key] = respData.x.map(
              (time: string, index: number) => ({
                name: time,
                value: respData[key][index] || 0,
              }),
            );
          }
        });

        setChartData(formattedData);
      }
    } catch (error) {
      console.error("Failed to fetch trend data:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [platform]);

  const renderChart = (
    chartKey: string,
    label: string,
    type: "line" | "bar",
    isFullWidth = false,
  ) => {
    const data = chartData[chartKey] || [];

    if (data.length === 0) {
      return null;
    }

    // 检查数据是否有效
    const hasValidData = data.some((item) => item.value !== 0);

    return (
      <Card
        key={chartKey}
        className={`w-full ${isFullWidth ? "col-span-full border-blue-500/30 bg-blue-50/5 dark:bg-blue-950/5" : ""}`}
      >
        <CardHeader className="py-3 px-4">
          <CardTitle
            className={`text-sm font-mono font-semibold ${isFullWidth ? "text-blue-600 dark:text-blue-400" : ""}`}
          >
            {label}{" "}
            {!hasValidData && (
              <span className="text-xs text-muted-foreground ml-2">
                (无数据)
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="px-4 pb-4">
          <ChartContainer
            config={chartConfig}
            className={`${isFullWidth ? "h-[300px]" : "h-[200px]"} w-full`}
          >
            <LineChart
              data={data}
              margin={{ left: 8, right: 8, top: 4, bottom: 4 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                dataKey="name"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={60}
                tickFormatter={(value) => value.slice(11, 16)}
                style={{ fontSize: "11px" }}
              />
              <YAxis
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                tickFormatter={(value) => value.toLocaleString()}
                style={{ fontSize: "11px" }}
                width={50}
              />
              <ChartTooltip
                content={<ChartTooltipContent />}
                cursor={{
                  stroke: "hsl(var(--muted-foreground))",
                  strokeWidth: 1,
                  strokeDasharray: "3 3",
                }}
              />
              <Line
                type="natural"
                dataKey="value"
                stroke={isFullWidth ? "#3b82f6" : "#10b981"}
                strokeWidth={isFullWidth ? 2.5 : 1.5}
                dot={false}
              />
            </LineChart>
          </ChartContainer>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="h-full p-6 overflow-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">趋势分析</h1>

        {/* 筛选控件 */}
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">
              平台:
            </span>
            <Select value={platform} onValueChange={setPlatform}>
              <SelectTrigger className="w-[100px] h-9">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="xr">xr</SelectItem>
                <SelectItem value="xl">xl</SelectItem>
                <SelectItem value="cm">cm</SelectItem>
                <SelectItem value="mg">mg</SelectItem>
                <SelectItem value="al">al</SelectItem>
                <SelectItem value="cx">cx</SelectItem>
                <SelectItem value="nx">nx</SelectItem>
                <SelectItem value="qc">qc</SelectItem>
                <SelectItem value="lc">lc</SelectItem>
                <SelectItem value="bxj">bxj</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-muted-foreground">
              时间:
            </span>
            <Input
              className="w-[100px] h-9"
              value={timeRange}
              onChange={(e) => setTimeRange(e.target.value)}
              placeholder="如: 3h"
            />
          </div>

          <Button onClick={fetchData} disabled={loading} size="sm">
            {loading ? "加载中..." : "刷新"}
          </Button>
        </div>
      </div>

      {/* 图表网格 */}
      {loading && Object.keys(chartData).length === 0 ? (
        <div className="flex items-center justify-center h-[400px]">
          <div className="text-center">
            <p className="text-muted-foreground">加载中...</p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/*{renderChart("maxMaxy2", "maxMaxy2", "line", true)}*/}

          {/* top4s 独占一行 */}
          {renderChart("top4s", "top4s", "line", true)}

          {/* maxMiny2 独占一行 */}
          {renderChart("maxMiny2", "maxMiny2", "line", true)}

          {/* minMiny2(minMin2 累计盈亏) 独占一行 */}
          {renderChart("minMiny2", "minMin2", "line", true)}

          {/* maxMiny3(maxMin3 累计盈亏) 独占一行 */}
          {renderChart("maxMiny3", "maxMin3", "line", true)}

          {/* g1 和 g2 一行两个(对比显示) */}
          {renderChart("g1", "g1", "line", false)}
          {renderChart("g2", "g2", "line", false)}

          {/* 其他指标(显式渲染过的不重复) */}
          {CHART_TYPES.filter(({ key }) =>
            (GRID_KEYS as readonly string[]).includes(key),
          ).map(({ key, label, type }) =>
            renderChart(key, label, type, false),
          )}
        </div>
      )}
    </div>
  );
}
